import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { useNavigate } from "react-router-dom";
import styles from "./SubscriptionPage.module.css";
import { useUtil } from "../../contexts/UtilContext";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import axios from "axios";
import ProOnboardModal from "../../components/FadeModalDialog/ProOnboardModal";
import ProrationSummaryModal from "./ProrationSummaryModal";
import ProRegOnboardModal from "./ProRegOnboardModal";

const SubscriptionPage = () => {
  const { accessToken, showLoginModal } = useAuth();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useUtil();

  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isProModalOpen, setIsProModalOpen] = useState(false);
  const [tempSelectedPlan, setTempSelectedPlan] = useState(null);
  const [isPaidUser, setIsPaidUser] = useState(false);
  const [showProrationModal, setShowProrationModal] = useState(false);
  const [prorationInvoice, setProrationInvoice] = useState(null);
  const [proRegOpen, setProRegOpen] = useState(false);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  useEffect(() => {
    if (accessToken && subscriptionPlans.length) {
      fetchUserSubscription();
    }
  }, [accessToken, subscriptionPlans]);

  const fetchSubscriptionPlans = async () => {
    try {
      showLoader();
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/professional/subscription-plans`
      );

      if (
        response.status === 200 &&
        response.data.success &&
        Array.isArray(response.data.plans)
      ) {
        setSubscriptionPlans(response.data.plans);
      } else {
        toast.error("No subscription plans available at the moment.");
      }
    } catch (error) {
      toast.error("Error fetching subscription plans. Please try again later.");
      console.error("Error fetching subscription plans:", error);
    } finally {
      hideLoader();
    }
  };

  const fetchUserSubscription = async () => {
    try {
      setIsLoadingSubscription(true);
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/professional/active-subscription`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.status === 200 && response.data.subscription) {
        const sub = response.data.subscription;
        setIsPaidUser(sub.plan_name !== "Basic and Free");
        if (sub.plan_name !== "Basic and Free") {
          setBillingCycle("yearly");
        }
        setActiveSubscription({
          planId: sub.plan_id,
          planName: sub.plan_name,
          renewalType: sub.renewal_type,
          status: sub.status,
          trialEndDate: sub.trial_end_date,
          expirationDate: sub.expiration_date,
          stripeSubscriptionId: sub.stripe_subscription_id,
        });
      } else {
        setFreePlanAsDefault();
        // setActiveSubscription({
        //   planId: 7,
        //   planName: "Basic and Free",
        //   renewalType: "monthly",
        //   status: "active",
        // });
        // setIsPaidUser(false);
      }
    } catch (error) {
      console.error("Error fetching active subscription:", error);
      setFreePlanAsDefault();
      // setActiveSubscription({
      //   planId: 7,
      //   planName: "Basic and Free",
      //   renewalType: "monthly",
      //   status: "active",
      // });
      // setIsPaidUser(false);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const setFreePlanAsDefault = () => {
    const fallbackPlan = subscriptionPlans.find(
      (plan) => plan.price === 0 && plan.renewal_type === "monthly"
    );

    if (fallbackPlan) {
      setActiveSubscription({
        planId: fallbackPlan.id,
        planName: fallbackPlan.name,
        renewalType: fallbackPlan.renewal_type,
        status: "active",
      });
      setIsPaidUser(false);
    } else {
      console.warn("No free monthly plan found, fallback logic needed.");
      setActiveSubscription(null);
      setIsPaidUser(false);
    }
  };

  const monthlyPlans = subscriptionPlans.filter(
    (p) => p.renewal_type === "monthly"
  );
  const yearlyPlans = subscriptionPlans.filter(
    (p) => p.renewal_type === "yearly"
  );
  const plansToDisplay =
    billingCycle === "monthly" ? monthlyPlans : yearlyPlans;

  const getProfessionalData = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/user/details`,
        {}, // ✅ send empty body if needed
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      return response.data; // ✅ axios returns parsed JSON directly
    } catch (error) {
      console.error("Error fetching professional data:", error);
      return null;
    }
  };

  const handleSubscribe = async (plan) => {
    if (!accessToken) {
      showLoginModal();
      return;
    }
    console.log("coming inside plan change call");
    console.log("new plan: ", plan);
    console.log("active plan: ", activeSubscription);
    if (activeSubscription && activeSubscription.planId !== plan.id) {
      console.log("inside handle plan change");
      await handlePlanChange(plan.id);
      return;
    }

    if (plan.name === "Basic and Free") {
      navigate("/");
      return;
    }

    if (plan.name.toLowerCase().includes("team")) {
      return;
    }

    setSelectedPlan(plan);

    try {
      showLoader();
      const proResponse = await getProfessionalData();
      if (proResponse?.professional) {
        const endpoint = "/payment/create-pro-subscription-checkout-session";
        const response = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}${endpoint}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ planId: plan.id }),
          }
        );

        const data = await response.json();

        if (data.paymentUrl) {
          window.location.href = data.paymentUrl;
        } else {
          toast.error("Failed to process subscription request.");
        }
      } else {
        setProRegOpen(true);
      }
    } catch (error) {
      console.error("Error during subscription:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      hideLoader();
    }
  };

  const handleManageSubscription = async () => {
    try {
      if (!activeSubscription || !activeSubscription.stripeSubscriptionId) {
        toast.error("No active subscription found.");
        return;
      }

      showLoader();

      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/payment/manage-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            subscriptionId: activeSubscription.stripeSubscriptionId,
          }),
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Failed to open subscription management.");
      }
    } catch (error) {
      console.error("Error opening subscription management:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      hideLoader();
    }
  };

  const handlePlanChange = async (newPlanId) => {
    try {
      console.log("handlePlanChange call: ", newPlanId);
      showLoader();
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/payment/check-prorate`,
        { planId: newPlanId },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (
        response.status === 200 &&
        response.data.success &&
        response.data.invoice
      ) {
        setProrationInvoice(response.data.invoice);
        setShowProrationModal(true);
      } else {
        toast.error("Failed to fetch prorate charges.");
      }
    } catch (error) {
      console.error("Error fetching proration info:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      hideLoader();
    }
  };

  const handleConfirmPlanChange = async () => {
    try {
      showLoader();
      const newPlanId = prorationInvoice?.newPlanId || tempSelectedPlan?.id;

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/payment/update-subscription`,
        { newPlanId: newPlanId },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200 && response.data.success) {
        toast.success("Subscription plan updated successfully!");
        fetchUserSubscription();
        setShowProrationModal(false);

        // refresh the page
        window.location.reload();
      } else {
        toast.error(
          response.data?.message ||
            "Failed to update subscription. Please try again."
        );
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.error(
        error.response?.data?.error ||
          "Something went wrong while updating your plan."
      );
    } finally {
      hideLoader();
    }
  };

  return (
    <div>
      <Navbar />
      <div className={styles.subscriptionPage}>
        <h1 className={styles.heading}>
          {accessToken ? "Your Current Plan" : "Choose Your Plan"}
        </h1>

        <div className={styles.toggleContainer}>
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`${styles.toggleButton} ${
              billingCycle === "monthly" ? styles.active : ""
            }`}
          >
            Pay Monthly
          </button>
          <button
            onClick={() => setBillingCycle("yearly")}
            className={`${styles.toggleButton} ${
              billingCycle === "yearly" ? styles.active : ""
            }`}
          >
            Pay Yearly - Best Value
          </button>
        </div>

        <div className={styles.planContainer}>
          {plansToDisplay
            .filter((plan) => !(isPaidUser && plan.name === "Basic and Free"))
            .sort((a, b) => a.price - b.price)
            .map((plan) => {
              const isCurrentPlan =
                activeSubscription &&
                (activeSubscription.planId === plan.id ||
                  (plan.price === 0 &&
                    activeSubscription.planName === "Basic and Free"));

              return (
                <div
                  key={plan.id}
                  className={`${styles.planCard} ${
                    selectedPlan?.id === plan.id ? styles.selected : ""
                  }`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  {plan.promotion && (
                    <div className={styles.diagonalBanner}>
                      {plan.promotion}
                    </div>
                  )}
                  <h2 className={styles.planName}>{plan.name}</h2>
                  <p className={styles.planDescription}>{plan.description}</p>
                  <p className={styles.planPrice}>
                    ${plan.price}
                    <span className={styles.duration}>
                      {plan.renewal_type === "monthly"
                        ? "per month"
                        : "per year"}
                    </span>
                  </p>
                  <ul className={styles.featureList}>
                    {plan.features.split(", ").map((feature, index) => (
                      <li key={index} className={styles.feature}>
                        ✓ {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      isCurrentPlan
                        ? toast.info("This is your current plan")
                        : handleSubscribe(plan);
                    }}
                    className={styles.subscribeButton}
                    disabled={
                      isCurrentPlan || plan.name.toLowerCase().includes("team")
                    }
                  >
                    {isCurrentPlan
                      ? "Your Current Plan"
                      : plan.name.toLowerCase().includes("basic")
                      ? "Use Free"
                      : plan.name.toLowerCase().includes("plus")
                      ? activeSubscription &&
                        activeSubscription.planName === "Firstland Plus" &&
                        activeSubscription.renewalType !== plan.renewal_type
                        ? plan.renewal_type === "yearly"
                          ? "Get Annual"
                          : "Shift to Monthly"
                        : "Get Plus"
                      : plan.name.toLowerCase().includes("team")
                      ? "Coming Soon"
                      : "Subscribe Now"}
                  </button>
                </div>
              );
            })}
        </div>
      </div>

      <ProRegOnboardModal
        open={proRegOpen}
        onClose={() => setProRegOpen(false)}
      />

      <ProOnboardModal
        open={isProModalOpen}
        onClose={() => setIsProModalOpen(false)}
      />

      <ProrationSummaryModal
        open={showProrationModal}
        invoice={prorationInvoice}
        currentSubcriptionplain={activeSubscription}
        onClose={() => setShowProrationModal(false)}
        onConfirm={handleConfirmPlanChange}
      />

      <Footer />
    </div>
  );
};

export default SubscriptionPage;
